import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, AlertTriangle, X } from "lucide-react";

const TYPE_LABELS = {
  text: "Texto corto", textarea: "Texto largo", number: "Número",
  date: "Fecha", boolean: "Sí / No", select: "Selección",
};

/** Panel de administración de secciones y preguntas custom
 *  para el Perfil de Viajero Externo. */
export default function CustomFieldsPanel({ baseCatalog, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customs, setCustoms] = useState({ sections: [], fields: [] });
  const [loading, setLoading] = useState(true);
  const [showSec, setShowSec] = useState(false);
  const [showFld, setShowFld] = useState(null); // {section_key}
  const [similarWarn, setSimilarWarn] = useState(null); // {payload, similares}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/viajeros-ext/custom-fields`);
      setCustoms(r.data || { sections: [], fields: [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitSection = async (label, force = false) => {
    try {
      await axios.post(`${API}/viajeros-ext/custom-sections`, { label, force });
      toast.success("Sección creada");
      setShowSec(false);
      setSimilarWarn(null);
      load(); onChanged && onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "similar_found") {
        setSimilarWarn({ kind: "section", payload: { label }, similares: detail.similares });
      } else {
        toast.error(detail || "Error");
      }
    }
  };

  const submitField = async (payload, force = false) => {
    try {
      await axios.post(`${API}/viajeros-ext/custom-fields`, { ...payload, force });
      toast.success("Pregunta creada");
      setShowFld(null);
      setSimilarWarn(null);
      load(); onChanged && onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "similar_found") {
        setSimilarWarn({ kind: "field", payload, similares: detail.similares });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error");
      }
    }
  };

  const removeField = async (fid) => {
    if (!window.confirm("¿Borrar esta pregunta custom? Los datos previos capturados por viajeros quedarán en el sistema pero dejarán de mostrarse en el formulario.")) return;
    try {
      await axios.delete(`${API}/viajeros-ext/custom-fields/${fid}`);
      toast.success("Pregunta borrada");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeSection = async (sid) => {
    if (!window.confirm("¿Borrar esta sección y todas sus preguntas custom?")) return;
    try {
      await axios.delete(`${API}/viajeros-ext/custom-sections/${sid}`);
      toast.success("Sección borrada");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // Todas las secciones = base + custom
  const allSections = [
    ...(baseCatalog?.sections || []).map((s) => ({ key: s.key, label: s.title || s.key, custom: false })),
    ...customs.sections.map((s) => ({ key: s.key, label: s.label, id: s.id, custom: true })),
  ];
  const fieldsBySection = customs.fields.reduce((acc, f) => {
    (acc[f.section_key] = acc[f.section_key] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="custom-fields-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-800">Campos personalizados</h2>
          <p className="text-xs text-zinc-500">Agrega preguntas o secciones nuevas al Perfil de Viajero. Los cambios aplican para TODOS los proyectos actuales y futuros.</p>
        </div>
        <Button onClick={() => setShowSec(true)} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-new-section">
          <Plus className="w-4 h-4 mr-1" /> Nueva sección
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-400 text-center py-8">Cargando…</p>
      ) : (
        <div className="space-y-3">
          {allSections.map((s) => {
            const flds = fieldsBySection[s.key] || [];
            const hasCustom = flds.length > 0;
            return (
              <div key={s.key} className="border rounded-md bg-white overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                  <div>
                    <div className="text-sm font-semibold text-zinc-700">{s.label}</div>
                    <div className="text-[11px] text-zinc-400">{s.custom ? "Sección custom" : "Sección base"} · {flds.length} campo{flds.length === 1 ? "" : "s"} custom</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setShowFld({ section_key: s.key, section_label: s.label })}
                      className="text-xs bg-white border rounded px-2 py-1 hover:bg-slate-100" data-testid={`btn-add-field-${s.key}`}>
                      <Plus className="w-3 h-3 inline mr-1" /> Pregunta
                    </button>
                    {s.custom && isAdmin && (
                      <button onClick={() => removeSection(s.id)}
                        className="text-xs text-red-700 hover:bg-red-50 rounded px-2 py-1" data-testid={`btn-del-section-${s.key}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                {hasCustom && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white text-zinc-500 text-xs">
                        <th className="text-left px-4 py-2">Pregunta</th>
                        <th className="text-left px-2 py-2">Tipo</th>
                        <th className="text-left px-2 py-2">Obligatorio</th>
                        <th className="px-2 py-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {flds.map((f) => (
                        <tr key={f.id} className="border-t" data-testid={`row-field-${f.k}`}>
                          <td className="px-4 py-2 text-zinc-700">{f.col}</td>
                          <td className="px-2 py-2 text-zinc-500 text-xs">{TYPE_LABELS[f.type] || f.type}</td>
                          <td className="px-2 py-2 text-xs">{f.required ? "Sí" : "—"}</td>
                          <td className="px-2 py-2 text-right">
                            {isAdmin && (
                              <button onClick={() => removeField(f.id)} className="text-red-700 hover:bg-red-50 rounded p-1" data-testid={`btn-del-field-${f.k}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Diálogo Nueva sección */}
      {showSec && (
        <SectionDialog onClose={() => setShowSec(false)} onSave={(label) => submitSection(label)} />
      )}
      {/* Diálogo Nueva pregunta */}
      {showFld && (
        <FieldDialog target={showFld} onClose={() => setShowFld(null)} onSave={(p) => submitField(p)} />
      )}
      {/* Popup de duplicado similar */}
      {similarWarn && (
        <SimilarDialog data={similarWarn} onCancel={() => setSimilarWarn(null)}
          onProceed={() => {
            if (similarWarn.kind === "section") submitSection(similarWarn.payload.label, true);
            else submitField(similarWarn.payload, true);
          }} />
      )}
    </div>
  );
}

function SectionDialog({ onClose, onSave }) {
  const [label, setLabel] = useState("");
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nueva sección</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label>Nombre de la sección</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej. Preferencias de vuelo" data-testid="new-section-label" autoFocus />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => label.trim() && onSave(label.trim())} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-section-save">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldDialog({ target, onClose, onSave }) {
  const [f, setF] = useState({
    col: "", type: "text", required: false, options: [""],
  });
  const submit = () => {
    if (!f.col.trim()) return toast.error("Escribe el texto de la pregunta");
    const payload = {
      section_key: target.section_key,
      col: f.col.trim(),
      type: f.type,
      required: !!f.required,
    };
    if (f.type === "select") {
      const opts = f.options.map((o) => o.trim()).filter(Boolean);
      if (opts.length < 2) return toast.error("Selección requiere al menos 2 opciones");
      payload.options = opts;
    }
    onSave(payload);
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nueva pregunta en &quot;{target.section_label}&quot;</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Texto de la pregunta</Label>
            <Input value={f.col} onChange={(e) => setF({ ...f, col: e.target.value })}
              placeholder="Ej. ¿Cuál es tu asiento preferido?" data-testid="new-field-col" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de respuesta</Label>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
                className="w-full border rounded-md p-2 text-sm" data-testid="new-field-type">
                {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <label className="flex items-end gap-2 pb-2">
              <input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} data-testid="new-field-required" />
              <span className="text-sm text-zinc-700">Obligatorio</span>
            </label>
          </div>
          {f.type === "select" && (
            <div>
              <Label>Opciones (mínimo 2)</Label>
              <div className="space-y-1 mt-1">
                {f.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <Input value={o} onChange={(e) => {
                      const arr = [...f.options]; arr[i] = e.target.value; setF({ ...f, options: arr });
                    }} placeholder={`Opción ${i + 1}`} data-testid={`new-field-opt-${i}`} />
                    <button className="p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => {
                      const arr = f.options.filter((_, j) => j !== i);
                      setF({ ...f, options: arr.length ? arr : [""] });
                    }}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button className="text-xs text-cyan-700 hover:underline"
                  onClick={() => setF({ ...f, options: [...f.options, ""] })}>+ Agregar opción</button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-field-save">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimilarDialog({ data, onCancel, onProceed }) {
  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Ya existe {data.kind === "section" ? "una sección parecida" : "una pregunta parecida"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-zinc-600">Encontramos {data.similares.length} coincidencia{data.similares.length === 1 ? "" : "s"} con nombre similar. Revisa antes de duplicar:</p>
          <ul className="border rounded-md divide-y">
            {data.similares.map((s, i) => (
              <li key={i} className="px-3 py-2 flex items-center justify-between">
                <span className="font-medium text-zinc-700">{s.col || s.label}</span>
                <span className="text-xs text-amber-700">Similitud {Math.round(s.similitud * 100)}%</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500">Puedes cancelar y usar la existente, o continuar de todos modos si es un caso distinto.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="similar-cancel">Cancelar</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={onProceed} data-testid="similar-proceed">Continuar de todos modos</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
